// src/pages/LoginPage.jsx
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { authAPI } from '../services/api';

export default function LoginPage() {
  const { login } = useAuth();
  const navigate = useNavigate();

  const [phone, setPhone] = useState('');
  const [password, setPassword] = useState('');
  const [showPwd, setShowPwd] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!phone || !password) { setError('Please fill in all fields.'); return; }
    setLoading(true);
    setError('');
    try {
      const user = await login(phone.trim(), password);

      if (user?.role === 'Rider') {
        setError('Riders must use the LogiDispatch mobile app to log in.');
        setLoading(false);
        return;
      }

      navigate('/');
    } catch (err) {
      setError(err?.response?.data?.error || 'Invalid credentials. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={styles.root}>
      {/* ── Left brand panel ─────────────────────────────────────────────── */}
      <div style={styles.left}>
        <div style={styles.leftInner}>
          <img src="/honcho1.png" alt="Logo" style={{ width: 300, height: 200, objectFit: 'contain' }} />
          <h1 style={styles.brandName}>Honcho Xpress</h1>
          <p style={styles.brandTagline}>Fast, Swift and Affordable Delivery<br />Management Platform</p>

        </div>
        <div style={styles.gridDots} />
      </div>

      {/* ── Right form panel ─────────────────────────────────────────────── */}
      <div style={styles.right}>
        <div style={styles.formCard}>
          <div style={styles.formHeader}>
            <h2 style={styles.formTitle}>Welcome back</h2>
            <p style={styles.formSub}>Sign in to your dispatcher or admin account</p>
          </div>

          {error && (
            <div style={styles.errorBox}>
              <span>⚠️</span> {error}
            </div>
          )}

          <form onSubmit={handleSubmit} style={styles.form}>
            <div className="form-group">
              <label className="form-label">Phone Number</label>
              <input
                className="form-input"
                type="tel"
                placeholder="+234 801 234 5678"
                value={phone}
                onChange={e => setPhone(e.target.value)}
                autoFocus
              />
            </div>

            <div className="form-group">
              <label className="form-label">Password</label>
              <div style={{ position: 'relative' }}>
                <input
                  className="form-input"
                  type={showPwd ? 'text' : 'password'}
                  placeholder="Enter your password"
                  value={password}
                  onChange={e => setPassword(e.target.value)}
                  style={{ paddingRight: 42 }}
                />
                <button
                  type="button"
                  onClick={() => setShowPwd(s => !s)}
                  style={styles.eyeBtn}
                  tabIndex={-1}
                >
                  {showPwd ? '🙈' : '👁️'}
                </button>
              </div>
            </div>

            <button
              type="submit"
              className="btn btn-primary btn-lg"
              style={{ width: '100%', marginTop: 8 }}
              disabled={loading}
            >
              {loading ? (
                <span style={{ display: 'flex', alignItems: 'center', gap: 8, justifyContent: 'center' }}>
                  <span style={styles.spinner} /> Signing in…
                </span>
              ) : 'Sign In'}
            </button>
          </form>


          <p style={styles.hint}>
            Only authorized dispatchers and admins can access this dashboard.
            Contact your system administrator for access.
          </p>
        </div>
      </div>
    </div>
  );
}

const styles = {
  root: { minHeight: '100vh', display: 'flex', fontFamily: 'var(--font-body)' },

  left: {
    width: '45%', background: 'var(--primary)',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    padding: '60px 48px', position: 'relative', overflow: 'hidden',
  },
  leftInner: { position: 'relative', zIndex: 1 },
  brandIcon: { fontSize: 52, marginBottom: 16 },
  brandName: {
    fontFamily: 'var(--font-display)', fontSize: 40, fontWeight: 800,
    color: 'var(--white)', letterSpacing: -1, marginBottom: 10, lineHeight: 1,
  },
  brandTagline: { fontSize: 16, color: 'var(--gray-400)', lineHeight: 1.5, marginBottom: 40 },
  features: { display: 'flex', flexDirection: 'column', gap: 16 },
  feature: { display: 'flex', alignItems: 'center', gap: 14 },
  featureIcon: {
    width: 36, height: 36, background: 'rgba(244,166,28,0.12)',
    borderRadius: 9, display: 'flex', alignItems: 'center',
    justifyContent: 'center', fontSize: 17, flexShrink: 0,
  },
  featureText: { fontSize: 13.5, color: 'var(--gray-300)', lineHeight: 1.4 },
  gridDots: {
    position: 'absolute', inset: 0,
    backgroundImage: 'radial-gradient(circle, rgba(255,255,255,0.04) 1px, transparent 1px)',
    backgroundSize: '28px 28px', pointerEvents: 'none',
  },

  right: {
    flex: 1, display: 'flex', alignItems: 'center',
    justifyContent: 'center', padding: '40px 32px', background: 'var(--gray-50)',
  },
  formCard: {
    background: 'var(--white)', borderRadius: 'var(--radius-xl)',
    padding: '40px', width: '100%', maxWidth: 420,
    boxShadow: 'var(--shadow-lg)', border: '1px solid var(--gray-200)',
  },
  formHeader: { marginBottom: 28 },
  formTitle: {
    fontFamily: 'var(--font-display)', fontSize: 28, fontWeight: 800,
    color: 'var(--gray-900)', letterSpacing: -0.5, marginBottom: 6,
  },
  formSub: { fontSize: 14, color: 'var(--gray-400)' },

  errorBox: {
    background: 'var(--danger-bg)', borderLeft: '4px solid var(--danger)',
    borderRadius: 'var(--radius-md)', padding: '12px 14px',
    fontSize: 13.5, color: 'var(--danger)', fontWeight: 500,
    marginBottom: 20, display: 'flex', gap: 8, alignItems: 'center',
  },
  form: { display: 'flex', flexDirection: 'column', gap: 18 },
  eyeBtn: {
    position: 'absolute', right: 12, top: '50%', transform: 'translateY(-50%)',
    background: 'none', border: 'none', cursor: 'pointer', fontSize: 16, padding: 2,
  },
  spinner: {
    display: 'inline-block', width: 16, height: 16,
    border: '2.5px solid rgba(10,22,40,0.2)', borderTopColor: 'var(--primary)',
    borderRadius: '50%', animation: 'spin 0.7s linear infinite',
  },

  riderNote: {
    marginTop: 20, padding: '10px 14px',
    background: '#EFF6FF', borderRadius: 8,
    display: 'flex', gap: 10, alignItems: 'center',
    fontSize: 13, color: '#1D4ED8', lineHeight: 1.5,
  },
  hint: {
    marginTop: 16, fontSize: 12, color: 'var(--gray-400)',
    textAlign: 'center', lineHeight: 1.6,
  },
};